import { Button } from "@brightagency/headstart-common";
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import EmailIcon from "@mui/icons-material/Email";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import InfoIcon from '@mui/icons-material/Info';
import LocalPhoneRoundedIcon from "@mui/icons-material/LocalPhoneRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VerifiedIcon from '@mui/icons-material/Verified';
import List from "@mui/material/List/List";
import React from "react";
import AnimateHeight from "react-animate-height";
import { ANALYTICS_IDS } from "../../../../../constants/analytics";
import { ListingLocation } from "../../../../../constants/types/acf";
import { useSortMode } from "../../../../../services/app-state";
import { getLocationDistanceFromUserString, useSuburbName } from "../../../../../utils";
import { useDisplayCoords } from "../../../../../utils/hooks";
import { UnstyledLink } from "../../../../atoms/UnstyledLink";
import {
    Outer,
    Header,
    Content,
    Buttons,
    IconAndDetail,
    Title,
    Distance,
    HeaderIcon,
} from "./styled";

interface Props {
    location: ListingLocation;
}

export const Location: React.FC<Props> = ({ location }) => {
    const coords = useDisplayCoords();
    const [isOpen, setIsOpen] = React.useState(false);
    const [sortMode] = useSortMode();
    const suburbName = useSuburbName();

    return (
        <Outer>
            <Header onClick={() => setIsOpen(a => !a)}>
                <Title>
                    {location.name}
                    {coords && (
                        <Distance>
                            {getLocationDistanceFromUserString(
                                location,
                                coords,
                                sortMode,
                                suburbName ?? "ERROR",
                            )}
                        </Distance>
                    )}
                </Title>
                <HeaderIcon>
                    {isOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </HeaderIcon>
            </Header>
            <AnimateHeight height={isOpen ? "auto" : 0}>
                <Content>
                    <div>
                        {location.additional_info && (
                            <IconAndDetail icon={<InfoIcon />}>
                                {location.additional_info}
                            </IconAndDetail>
                        )}
                        {location.phone_number && (
                            <IconAndDetail icon={<LocalPhoneRoundedIcon />}>
                                Phone{"  "}
                                <a href={`tel:${location.phone_number}`}>{location.phone_number}</a>
                            </IconAndDetail>
                        )}
                        {location.address && (
                            <IconAndDetail icon={<LocationOnRoundedIcon />}>
                                {location.address_title && <strong>{location.address_title} </strong>}
                                <a
                                    data-analytics-id={ANALYTICS_IDS.serviceLocationMap}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={`https://maps.google.com/?q=${encodeURIComponent(
                                        location.address.address,
                                    )}`}
                                >
                                    {location.address.address}
                                </a>
                            </IconAndDetail>
                        )}
                        {location.service_provided_by && (
                            <IconAndDetail icon={<VerifiedIcon />}>
                                Service provided by {location.service_provided_by}
                            </IconAndDetail>
                        )}
                        {location.services_at_this_location && (
                            <IconAndDetail icon={<ChecklistRtlIcon />}>
                                Services at this location:
                                <List>
                                    {location.services_at_this_location?.map((service) => (
                                        <li key={service['service']}>
                                            {service['service']}
                                        </li> 
                                    ))}
                                </List> 
                            </IconAndDetail>
                        )}  
                    </div>
                    <Buttons>
                        {location.location_email && (
                            <UnstyledLink
                                data-analytics-id={ANALYTICS_IDS.serviceLocationEmail}
                                target="_blank"
                                rel="noopener noreferrer"
                                href={`mailto:${location.location_email}`}
                                style={{
                                    textDecoration: "none",
                                    display: "block",
                                }}
                            >
                                <Button appearance="outline" iconBefore={<EmailIcon />}>
                                    Send email
                                </Button>
                            </UnstyledLink>
                        )}
                        {location.location_website && (
                            <UnstyledLink
                                data-analytics-id={ANALYTICS_IDS.serviceWebsite}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    textDecoration: "none",
                                    display: "block",
                                }}
                                href={location.location_website}
                            >
                                <Button appearance="outline" iconBefore={<OpenInNewIcon />}>
                                    Visit website
                                </Button>
                            </UnstyledLink>
                        )}
                    </Buttons>
                </Content>
            </AnimateHeight>
        </Outer>
    );
};
